
import { useQuery } from "@tanstack/react-query";
import { supabase } from "../integrations/supabase/client";
import { useAuth } from "../contexts/AuthContext";
import { getUserSubscriptionInfo, checkRemainingQuota } from "../services/subscriptionService";
import { usePaymentPlatform } from "../contexts/PaymentPlatformContext";

interface SubscriptionData {
  subscription_tier: 'free' | 'standard' | 'premium' | 'ultimate';
  paypal_subscription_id?: string;
  stripe_customer_id?: string;
  payment_provider: string;
  subscription_source?: string;
}

interface EnhancedSubscriptionData {
  currentTier: 'free' | 'standard' | 'premium' | 'ultimate';
  subscription_tier: 'free' | 'standard' | 'premium' | 'ultimate';
  paypal_subscription_id?: string;
  stripe_customer_id?: string;
  payment_provider: string;
  subscription_source?: string;
}

export function useSubscription() {
  const { user } = useAuth();
  
  const { isNative, nativeTier } = usePaymentPlatform();
  
  const subscriptionQuery = useQuery({
    queryKey: ['subscription', user?.id],
    queryFn: async () => {
      if (!user?.id) {
        throw new Error('User not authenticated');
      }

      console.log('Fetching subscription for user:', user.id);
      
      const { data, error } = await supabase
        .from('users')
        .select('subscription_tier, paypal_subscription_id, stripe_customer_id, payment_provider, subscription_source')
        .eq('id', user.id)
        .single();

      if (error) {
        console.error('Error fetching subscription:', error);
        throw new Error(`Error fetching subscription: ${error.message}`);
      }

      if (!data) {
        throw new Error('No subscription data found');
      }

      console.log('Subscription data:', data);
      return data as SubscriptionData;
    },
    enabled: !!user?.id,
    retry: 3,
    retryDelay: 1000,
  });

  const quotaQuery = useQuery({
    queryKey: ['quota', user?.id],
    queryFn: async () => {
      if (!user?.id) {
        throw new Error('User not authenticated');
      }
      return await checkRemainingQuota(user.id);
    },
    enabled: !!user?.id,
    retry: 2,
    retryDelay: 1000,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  // NOTE: Direct client-side updates to `users.subscription_tier` were removed
  // as part of the security hardening for finding `users_table_subscription_tier_self_upgrade`.
  // Subscription tier changes are server-side only — handled by the PayPal/RevenueCat
  // webhooks and admin RPCs. The DB trigger `protect_users_sensitive_columns_trigger`
  // also blocks any client attempt to mutate billing/subscription columns.


  // Transform the subscription data to include currentTier for backward compatibility
  // Use the HIGHEST tier between RevenueCat (native) and DB to avoid downgrading
  // users who were upgraded via admin/web but don't have a RevenueCat subscription
  const tierRank = { free: 0, standard: 1, premium: 2, ultimate: 3 } as const;
  const dbTier = subscriptionQuery.data?.subscription_tier || 'free';
  const rcTier = (isNative && nativeTier) ? nativeTier : 'free';
  const effectiveTier = (tierRank[rcTier as keyof typeof tierRank] ?? 0) >= (tierRank[dbTier as keyof typeof tierRank] ?? 0)
    ? rcTier
    : dbTier;
  
  const enhancedSubscription: { data: EnhancedSubscriptionData } | undefined = 
    subscriptionQuery.data ? {
      data: {
        ...subscriptionQuery.data,
        currentTier: effectiveTier || subscriptionQuery.data.subscription_tier,
        subscription_tier: effectiveTier || subscriptionQuery.data.subscription_tier
      }
    } : (isNative && nativeTier && nativeTier !== 'free') ? {
      data: {
        currentTier: nativeTier,
        subscription_tier: nativeTier,
        payment_provider: 'revenuecat',
        subscription_source: isNative ? 'google' : 'apple',
      }
    } : undefined;

  const isAnyLoading = subscriptionQuery.isLoading || quotaQuery.isLoading;

  return {
    subscription: enhancedSubscription,
    quota: quotaQuery.data,
    isLoading: subscriptionQuery.isLoading,
    isAnyLoading,
    error: subscriptionQuery.error,
  };
}
